youtube
<script setup>
import ArrowBottomIcon from "@/components/Icons/ArrowBottomIcon.vue";
import {defineEmits, defineProps, ref} from "vue";
import PlusBoldIcon from "@/components/Icons/PlusBoldIcon.vue";
import { t } from "@/controllerLK/AvalLocalization";
import DotsIcon from "@/components/Icons/DotsIcon.vue";
import IconTrash from "@/components/Icons/IconTrash.vue";
import { useNotificationsStore } from "@/store/notifications";
import { useMapStore } from "@/store/Map/map-store";

const isShowDropdown = ref(false);

const emit = defineEmits(["close-news-list", "set-active-map", "show-create-map-modal", "get-project-map-files"]);

const props = defineProps({
  activeMap: {
    type: [Object, null],
    required: true,
  },
  mapList: {
    type: Array,
    required: true
  }
});

const notificationStore = useNotificationsStore();
const mapStore = useMapStore();

// Флаг отображения контекстного меню
const isShowContextMenu = ref(false);
// Элемент списка на котором сейчас находится курсор
const hoveredItem = ref(null);
// Выбранный объект контекстного меню
const contextMenuItem = ref(null);
// Позиция контекстного меню
const contextMenuPosition = ref({ top: 0, left: 0 });

// Флаг отправки запроса удаления карты
const isStartDeleteMapQuery = ref(false);

// Переключение отображения контекстного меню
const toggleContextMenu = (item, event = null) => {
  if (event) {
    event.stopPropagation();
  }

  if (isShowContextMenu.value && contextMenuItem.value === item) {
    isShowContextMenu.value = false;
    contextMenuItem.value = null;
  } else {
    contextMenuItem.value = item;

    // Координаты кнопки с иконкой
    const rect = event.currentTarget.getBoundingClientRect();
    contextMenuPosition.value = {
      top: rect.bottom + 4,
      left: rect.left
    };

    isShowContextMenu.value = true;
  }
};

// Закрытие контекстного меню при клике вне его
const closeContextMenu = (event) => {
  if (event) {
    event.stopPropagation();
  }
  isShowContextMenu.value = false;
  contextMenuItem.value = null;
};

// Функция установки активной карты
const setActiveMap = (map) => {
  isShowDropdown.value = false;
  emit("close-news-list");
  emit("set-active-map", map);
  mapSearchQuery.value = "";
  localActiveMap.value = [map];
};

// Закрытие выпадающего списка карт
const closeMapSelector = () => {
  // Закрываем дропдаун только если не открыто контекстное меню или окно удаления карты
  if (!isShowContextMenu.value && !isShowDeleteMapModal.value) {
    isShowDropdown.value = false;
  }
};

const mapSearchQuery = ref("");
const localActiveMap = ref([props.activeMap]);

const showCreateAppModal = () => {
  isShowDropdown.value = false;
  emit("show-create-map-modal");
};

// Модальное окно подтверждения удаления карты.
const isShowDeleteMapModal = ref(false);

// Открытие модального окна удаления карты.
const toggleShowDeleteMapModal = () => {
  if (isShowDeleteMapModal.value) {
    isShowDeleteMapModal.value = false;
    isShowContextMenu.value = false;
    contextMenuItem.value = null;
  } else {
    isShowDeleteMapModal.value = true;
    // Только закрытие контекстного меню, без удаления объекта.
    isShowContextMenu.value = false;
  }
};

// Обработчик отмены удаления карты
const handleCancelDelete = (event) => {
  if (event) {
    event.stopPropagation();
    event.preventDefault();
  }
  isShowDeleteMapModal.value = false;
  isShowContextMenu.value = false;
  contextMenuItem.value = null;
  setTimeout(() => {
    isShowDropdown.value = true;
  }, 0);
};

// Обработчик подтверждения удаления карты
const handleConfirmDelete = async () => {

  try {
    isStartDeleteMapQuery.value = true;
    await mapStore.deleteMap(contextMenuItem.value.project_id, contextMenuItem.value.name);
    emit("set-active-map", null);
    notificationStore.createNotify(t("map_selector.toast.delete_success", contextMenuItem.value.name), "success");
  } catch (e) {
    console.log(e);
  } finally {
    isStartDeleteMapQuery.value = false;
    isShowDeleteMapModal.value = false;
    isShowContextMenu.value = false;
    contextMenuItem.value = null;
    isShowDropdown.value = false;
  }
};
</script>

<template>
  <SimpleDropdown
    v-model:search-bar-query="mapSearchQuery"
    trigger-height="24px"
    width="200px"
    max-height="468px"
    :is-visible="isShowDropdown"
    :use-cross-icon="false"
    :title="null"
    :use-searchbar="mapList.length >= 12"
    teleport="#app"
    @close="closeMapSelector"
  >
    <template #trigger>
      <span
        class="active-map"
        :class="{'active-map--show': isShowDropdown}"
        @click="isShowDropdown = !isShowDropdown"
      >
        <span class="active-map__title">{{ activeMap?.name }}</span>
        <ArrowBottomIcon />
      </span>
    </template>
    <template #dropdown-item>
      <ListItem
        v-for="item in mapList.filter((map) => map.name.toLowerCase().includes(mapSearchQuery.toLowerCase()))"
        :key="item.name"
        :model-value="localActiveMap"
        :item="item"
        return-object
        class="map-selector__item"
        @click.self="setActiveMap(item)"
        @mouseenter="hoveredItem = item"
        @mouseleave="hoveredItem = null"
      >
        {{ item.name }}
        <DotsIcon
          :class="{
            'map-selector__options--active': item.map_file_path === localActiveMap[0].map_file_path,
            'map-selector__options--visible': hoveredItem === item || contextMenuItem === item
          }"
          class="map-selector__options"
          @click="toggleContextMenu(item, $event)"
        />
      </ListItem>
    </template>
    <template #footer>
      <ButtonAccentPrimary
        size="large"
        class="map-selector__add-button"
        @click="showCreateAppModal"
      >
        <PlusBoldIcon class="map-selector__small-plus" /> {{ t("no_map_view.add_region") }}
      </ButtonAccentPrimary>
    </template>
  </SimpleDropdown>

  <!-- Контекстное меню -->
  <Teleport to="#app">
    <div
      v-if="isShowContextMenu"
      class="context-menu-overlay"
      @click="closeContextMenu"
    >
      <div
        class="map-context-menu"
        :style="{
          top: `${contextMenuPosition.top}px`,
          left: `${contextMenuPosition.left}px`
        }"
        @click.stop
      >
        <div
          class="map-context-menu__item"
          @click="toggleShowDeleteMapModal"
        >
          <IconTrash />
          {{ t('map_selector.button.delete') }}
        </div>
      </div>
    </div>
  </Teleport>

  <!-- Модальное окно удаления карты  -->
  <Modal
    v-if="isShowDeleteMapModal"
    :title="t('map_selector.text.title_delete_map')"
    :confirm-text="t('map_selector.button.delete')"
    :cancel-text="t('map_selector.button.cancel')"
    teleport="#app"
    :is-confirm-pressed="isStartDeleteMapQuery"
    @cancel="handleCancelDelete"
    @confirm="handleConfirmDelete"
    @click.stop
  >
    <div @click.stop>
      {{ t('map_selector.text.delete_map', contextMenuItem.name) }}
    </div>
  </Modal>
</template>

<style scoped lang="scss">

.context-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  background: transparent;
}

.map-context-menu {
  position: fixed;
  background-color: var(--background-bg-white);
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  border-radius: 8px;
  box-shadow: 1px 2px 10px 0 var(--gray-gray50);
  z-index: 10000;
  min-width: 200px;

  &__item {
    display: flex;
    align-items: center;
    gap: 10px;
    border-radius: 8px;
    padding: 6px 14px;
    color: var(--text-text-main);
    white-space: nowrap;
    cursor: pointer;

    svg {
      color: var(--icon-icon-secondary);
    }

    &:hover {
      background-color: var(--background-bg-hover);
    }
  }
}

.active-map {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px 4px 10px;
  cursor: pointer;
  color: #7B87A5;
  border-radius: 20px;
  gap: 4px;
  max-width: 200px;

  &__title {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
  }

  &__item {
    display: flex;
    padding: 6px 14px;
    border-radius: 8px;
    cursor: pointer;

    &--active {
      color: var(--text-text-on);
      &:hover { background-color: var(--background-bg-active-hover) }
    }

    &:not(&--active):hover {
      background-color: var(--background-bg-hover2);
    }
  }

  &:hover {
    background: #F7F8FD;
  }

  &--show {
    color: #1F9AFF;
    background: #FFFFFF;

    & svg {
      transform: rotate(180deg);
    }
  }
}
.map-selector{

  &__add-button {
    width: 100%;
  }

  &__small-plus {
    width: 12px;
    height: 12px;
  }

  &__item {
    display: flex;
    justify-content: space-between;
    position: relative;
  }

  &__options {
    min-width: 14px;
    min-height: 14px;
    color: var(--icon-icon-secondary);
    visibility: hidden;
    cursor: pointer;
    
    &--visible {
      visibility: visible;
    }

    &--active {
      color: var(--icon-icon-on);
    }
  }
}
</style>